// ===================================================================
// KAIXO - Pestaña Estadísticas
// ===================================================================
// Gráficas SVG inline (sin librería) con la serie temporal de los
// últimos N días: nuevos usuarios, mensajes y llamadas por día.
// Útil para mostrar a inversores la evolución real del producto.
// ===================================================================

import React, { useEffect, useState, useCallback, useId } from 'react';
import { adminStatsTimeseries } from '../../../services/adminApi';
import './AdminStatsSection.css';

const RANGES = [
    { id: 7,  label: '7 días' },
    { id: 14, label: '14 días' },
    { id: 30, label: '30 días' },
    { id: 60, label: '60 días' },
    { id: 90, label: '90 días' }
];

const METRICS = [
    { key: 'users',    label: 'Nuevos usuarios', color: '#F5A000' },
    { key: 'messages', label: 'Mensajes',        color: '#22c55e' },
    { key: 'calls',    label: 'Llamadas',        color: '#3b82f6' }
];

function formatDate(d) {
    const date = new Date(d);
    return `${date.getDate()}/${date.getMonth() + 1}`;
}

export default function AdminStatsSection() {
    const [days, setDays] = useState(30);
    const [series, setSeries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const reload = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const { data } = await adminStatsTimeseries(days);
            setSeries(data?.series || []);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => { reload(); }, [reload]);

    return (
        <div className="ast-wrap">
            <div className="ast-header">
                <p className="ast-intro">
                    Evolución diaria de las métricas clave. Datos directos de la BD —
                    sin caché ni acumulados.
                </p>
                <div className="ast-range">
                    {RANGES.map(r => (
                        <button
                            key={r.id}
                            className={`ast-range-btn ${days === r.id ? 'is-active' : ''}`}
                            onClick={() => setDays(r.id)}
                        >{r.label}</button>
                    ))}
                </div>
            </div>

            {loading && <div className="ap-loading">Cargando series…</div>}
            {error && <div className="ap-error">{error}</div>}

            {!loading && !error && series.length > 0 && (
                <div className="ast-charts">
                    {METRICS.map(m => (
                        <Chart
                            key={m.key}
                            title={m.label}
                            color={m.color}
                            data={series.map(s => ({ x: s.date, y: s[m.key] || 0 }))}
                        />
                    ))}
                </div>
            )}

            {!loading && series.length === 0 && (
                <div className="ast-empty">No hay datos para el rango seleccionado.</div>
            )}
        </div>
    );
}

function Chart({ title, color, data }) {
    // useId garantiza un id único por instancia incluso si dos charts
    // comparten color; un mismo `id` en varios <linearGradient> del DOM
    // hace que solo el primero pinte.
    const gradId = useId();
    const W = 720;
    const H = 220;
    const PAD_L = 40, PAD_R = 12, PAD_T = 16, PAD_B = 28;
    const innerW = W - PAD_L - PAD_R;
    const innerH = H - PAD_T - PAD_B;

    if (data.length === 0) return null;

    const maxY = Math.max(1, ...data.map(d => d.y));
    const total = data.reduce((acc, d) => acc + d.y, 0);
    const avg = Math.round(total / data.length);
    const last = data[data.length - 1]?.y || 0;
    const prev = data[data.length - 2]?.y || 0;
    const trend = prev === 0 ? 0 : Math.round(((last - prev) / prev) * 100);

    const xAt = (i) => PAD_L + (i / Math.max(1, data.length - 1)) * innerW;
    const yAt = (v) => PAD_T + innerH - (v / maxY) * innerH;

    const pathD = data.map((d, i) =>
        `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)} ${yAt(d.y).toFixed(1)}`
    ).join(' ');

    const areaD = `${pathD} L ${xAt(data.length - 1).toFixed(1)} ${(PAD_T + innerH).toFixed(1)} L ${xAt(0).toFixed(1)} ${(PAD_T + innerH).toFixed(1)} Z`;

    // Y-axis ticks (4 niveles)
    const yTicks = [0, 0.25, 0.5, 0.75, 1].map(p => ({
        y: PAD_T + innerH - p * innerH,
        label: Math.round(p * maxY)
    }));

    // X-axis ticks (cada ~7 puntos)
    const step = Math.max(1, Math.floor(data.length / 7));
    const xTicks = [];
    for (let i = 0; i < data.length; i += step) xTicks.push({ i, label: formatDate(data[i].x) });

    return (
        <div className="ast-chart">
            <div className="ast-chart-head">
                <h4>{title}</h4>
                <div className="ast-chart-stats">
                    <span>Total: <strong>{total.toLocaleString()}</strong></span>
                    <span>Promedio: <strong>{avg}</strong></span>
                    <span>Hoy: <strong>{last}</strong></span>
                    {trend !== 0 && (
                        <span className={trend > 0 ? 'ast-trend-up' : 'ast-trend-down'}>
                            {trend > 0 ? '▲' : '▼'} {Math.abs(trend)}%
                        </span>
                    )}
                </div>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} className="ast-svg" preserveAspectRatio="none">
                <defs>
                    <linearGradient id={`grad-${gradId}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity="0.35" />
                        <stop offset="100%" stopColor={color} stopOpacity="0.02" />
                    </linearGradient>
                </defs>

                {/* Y grid */}
                {yTicks.map((t, i) => (
                    <g key={i}>
                        <line x1={PAD_L} x2={W - PAD_R} y1={t.y} y2={t.y}
                              stroke="rgba(0,0,0,0.06)" strokeDasharray="3 3" />
                        <text x={PAD_L - 6} y={t.y + 3} fontSize="10"
                              textAnchor="end" fill="#888">{t.label}</text>
                    </g>
                ))}

                {/* X labels */}
                {xTicks.map((t, i) => (
                    <text key={i} x={xAt(t.i)} y={H - 8} fontSize="10"
                          textAnchor="middle" fill="#888">{t.label}</text>
                ))}

                {/* Area */}
                <path d={areaD} fill={`url(#grad-${gradId})`} />
                {/* Line */}
                <path d={pathD} fill="none" stroke={color} strokeWidth="2" />
                {/* Last point */}
                <circle cx={xAt(data.length - 1)} cy={yAt(last)} r="4" fill={color} />
            </svg>
        </div>
    );
}
